import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Auth, I18n } from 'aws-amplify';

import {
  Button,
  CheckboxField,
  Divider,
  Flex,
  Link,
  Text,
  useAuthenticator,
  useTheme,
  View,
} from '@aws-amplify/ui-react';

import { useToast } from 'contexts/toastContext';
import { useLabelOptions } from 'contexts/labelOptionsContext';
import { useAuthContext } from 'contexts/authContext';
import { useLocaleState } from 'contexts/localeContext';
import { getLabelOptions } from 'labels/hosts';

const TermsCheckbox = ({ checked, hasError, onChange, termsLink, tokens, forgotPasswordButton }) => {
  const [portalTarget, setPortalTarget] = React.useState(null);

  React.useEffect(() => {
    // Find the submit button and insert checkbox container before it
    const submitButton = document.querySelector('[data-amplify-authenticator-signin] .amplify-button--primary');
    if (submitButton) {
      let container = document.getElementById('terms-checkbox-portal');
      if (!container) {
        container = document.createElement('div');
        container.id = 'terms-checkbox-portal';
        submitButton.parentNode.insertBefore(container, submitButton);
      }
      setPortalTarget(container);
    }
  }, []);

  if (!portalTarget || !termsLink) return null;

  return ReactDOM.createPortal(
    <Flex
      direction={{ base: 'column', small: 'row' }}
      justifyContent={{ base: 'center', small: 'space-between' }}
      alignItems="center"
      paddingTop={{ base: tokens.space.medium, small: '0' }}
      paddingBottom={tokens.space.medium}
      wrap="wrap"
    >
      <Flex gap="0rem" alignItems="center">
        <CheckboxField
          checked={checked}
          hasError={hasError}
          onChange={onChange}
          label={<Text color={hasError ? tokens.colors.font.error : 'inherit'}>{I18n.get('auth.accept')}</Text>}
          name="agree"
          value="yes"
        />
        &nbsp;
        <Link href={termsLink} isExternal>
          {I18n.get('auth.terms')}
        </Link>
      </Flex>
      {forgotPasswordButton}
    </Flex>,
    portalTarget,
  );
};

const SignInFooter = () => {
  const { toResetPassword } = useAuthenticator();
  const { labelOptions } = useLabelOptions();
  const { tokens } = useTheme();
  const { setToastData } = useToast();
  const { setIsResetPasswordFlow } = useAuthContext();
  const { rtl } = useLocaleState();

  const [checked, setChecked] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  const handleCheckboxChange = React.useCallback((e) => {
    setChecked(e.target.checked);
    setHasError(false);
  }, []);

  const handleResetPassword = React.useCallback(() => {
    setIsResetPasswordFlow(true);
    toResetPassword();
  }, [toResetPassword, setIsResetPasswordFlow]);

  const handler = React.useCallback(() => {
    setHasError(true);
    setToastData({
      variant: 'error',
      message: I18n.get('auth.terms.toastErrorMessage'),
      heading: '',
    });
  }, [setToastData]);

  const fakeCheckbox = document.querySelectorAll('[type="hiddenField"]')[0];

  if (fakeCheckbox) {
    fakeCheckbox.addEventListener('invalid', handler);
  }

  const handleSSOLogin = React.useCallback(() => {
    const { SSOProvider: customProvider } = getLabelOptions();
    if (checked) {
      Auth.federatedSignIn({ customProvider });
    } else {
      handler();
    }
  }, [handler, checked]);

  React.useEffect(() => {
    if (fakeCheckbox) {
      checked ? (fakeCheckbox.value = checked) : (fakeCheckbox.value = '');
    }
  }, [checked, fakeCheckbox]);

  React.useEffect(
    () => () => {
      setToastData(null);
    },
    [setToastData],
  );

  return (
    <View padding={`0px ${tokens.components.authenticator.form.padding}`}>
      <TermsCheckbox
        checked={checked}
        hasError={hasError}
        onChange={handleCheckboxChange}
        termsLink={labelOptions.termsAndConditionLink}
        tokens={tokens}
        forgotPasswordButton={
          <Button
            onClick={handleResetPassword}
            variation="link"
            margin={{ base: '0', small: rtl ? '0 auto 0 0' : '0 0 0 auto' }}
          >
            {I18n.get('auth.forgotPassword')}
          </Button>
        }
      />

      {labelOptions.isSsoLogin && (
        <Flex textAlign="center" direction="column">
          <Divider label={I18n.get('or')} orientation="horizontal" />
          <Button
            fontWeight="normal"
            onClick={handleSSOLogin}
            size="default"
            isFullWidth={true}
            marginTop={tokens.space.xxxs}
          >
            {I18n.get('auth.login')}
          </Button>
          {labelOptions.supportEmail && (
            <Text fontSize={tokens.fontSizes.small} marginTop={tokens.space.small}>
              {I18n.get('auth.needHelp')}{' '}
              <Link href={`mailto:${labelOptions.supportEmail}`}>{I18n.get('auth.support')}</Link>
            </Text>
          )}
        </Flex>
      )}
    </View>
  );
};

export default SignInFooter;
